/**
 * The one fire-and-forget analytics sink, and the injectable sink type that
 * goes with it: resolve the lazily created client, emit one registered event,
 * and never make the caller wait.
 *
 * **Fire-and-forget is the contract, not an oversight.** `getAnalytics()`
 * dynamically imports the Mixpanel SDK, so awaiting it at a call site would put
 * a chunk fetch in front of a click handler or an effect. Telemetry must never
 * delay, block or reject into the flow it is measuring.
 *
 * Deliberately NOT re-exported from `./index`: several suites replace that
 * module wholesale with a factory mock that supplies only `getAnalytics`, and
 * an importer of `trackEvent` from there would get `undefined` under those
 * mocks. Importing this module instead leaves those mocks working — the
 * `getAnalytics` they stub is the one this module calls.
 */
import type { AnalyticsEventName, AnalyticsEventProps } from "@reflect/domain";

import { getAnalytics } from "./index";

/**
 * Typed analytics sink. Components and hooks accept one as an optional prop
 * (defaulting to {@link trackEvent}) so tests can observe payloads directly
 * rather than inferring them from a rendered string.
 */
export type TrackFn = <N extends AnalyticsEventName>(
  name: N,
  props: AnalyticsEventProps<N>,
) => void;

/**
 * Emit one registered event through the browser analytics client.
 *
 * @param name - the registered event name.
 * @param props - the payload the registry declares for that event.
 */
export function trackEvent<N extends AnalyticsEventName>(
  name: N,
  props: AnalyticsEventProps<N>,
): void {
  // A client that failed to load drops the event rather than surfacing an
  // unhandled rejection; the next call retries the load (see `getAnalytics`).
  getAnalytics()
    .then((analytics) => analytics.track(name, props))
    .catch(() => undefined);
}
